import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@shared/api/client';

/**
 * Reconocimiento de voz con tres motores intercambiables:
 *
 *  - 'whisper-server' → DEFAULT. Graba audio, lo convierte a WAV PCM 16k mono
 *                       en el navegador y lo envía al endpoint Laravel
 *                       `/api/v1/ai/transcribe` que ejecuta whisper.cpp local.
 *                       Funciona en CUALQUIER navegador (Edge incluido).
 *
 *  - 'whisper-local'  → Whisper dentro del navegador vía transformers.js
 *                       (WASM). No requiere backend ni internet.
 *                       Falla en Edge por WebNN incompat con QDQ.
 *
 *  - 'google'         → Web Speech API. Rápido pero depende de servidores
 *                       de Google. Falla en Edge con error `network`.
 *
 * Persistimos la elección en localStorage.
 */

export type SttEngine = 'whisper-server' | 'whisper-local' | 'google';

const STORAGE_KEY = 'intraeco.stt.engine';
const WHISPER_LOCAL_MODEL = 'Xenova/whisper-tiny';
const TARGET_SAMPLE_RATE = 16000;

type WhisperPipeline = (
  input: Float32Array,
  options?: Record<string, unknown>,
) => Promise<{ text: string } | { text: string }[]>;

/* ─────────────────────────── transformers.js singleton ─────────────────────────── */

let whisperPromise: Promise<WhisperPipeline> | null = null;
let whisperProgressCb: ((p: number) => void) | null = null;

function resetWhisperLocal(): void {
  whisperPromise = null;
}

async function getWhisperLocal(): Promise<WhisperPipeline> {
  if (whisperPromise) return whisperPromise;

  whisperPromise = (async () => {
    const { pipeline, env } = await import('@huggingface/transformers');
    env.allowRemoteModels = true;

    return (await pipeline('automatic-speech-recognition', WHISPER_LOCAL_MODEL, {
      device: 'wasm',
      dtype: 'q8',
      progress_callback: (data: any) => {
        if (data?.status === 'progress' && typeof data.progress === 'number') {
          whisperProgressCb?.(data.progress);
        } else if (data?.status === 'done') {
          whisperProgressCb?.(100);
        }
      },
    })) as unknown as WhisperPipeline;
  })();

  return whisperPromise;
}

/* ─────────────────────────── audio helpers ─────────────────────────── */

/** Decodifica un blob (webm/ogg/mp3/wav) a Float32 mono 16 kHz. */
async function blobToMonoFloat32(blob: Blob): Promise<Float32Array> {
  const arrayBuffer = await blob.arrayBuffer();
  const ctx = new AudioContext();
  const decoded = await ctx.decodeAudioData(arrayBuffer.slice(0));
  ctx.close();

  if (decoded.sampleRate === TARGET_SAMPLE_RATE && decoded.numberOfChannels === 1) {
    return decoded.getChannelData(0);
  }

  const offline = new OfflineAudioContext(
    1,
    Math.ceil(decoded.duration * TARGET_SAMPLE_RATE),
    TARGET_SAMPLE_RATE,
  );
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start(0);
  const rendered = await offline.startRendering();
  return rendered.getChannelData(0);
}

/** Encapsula un Float32 (-1..1) en un WAV PCM 16-bit, 16 kHz, mono. */
function floatToWavBlob(samples: Float32Array, sampleRate = TARGET_SAMPLE_RATE): Blob {
  const numFrames = samples.length;
  const numChannels = 1;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = numFrames * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeStr = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };

  // RIFF header
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  // fmt subchunk
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);            // subchunk1 size (16 = PCM)
  view.setUint16(20, 1, true);             // audio format = PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  // data subchunk
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);

  // Samples
  let off = 44;
  for (let i = 0; i < numFrames; i++, off += 2) {
    let s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

/* ─────────────────────────── hook ─────────────────────────── */

export function useSpeechToText(lang: string = 'es-ES') {
  // Solo whisper-server (Whisper en backend) está expuesto al usuario.
  // Si localStorage tenía 'google' o 'whisper-local' de versiones anteriores,
  // se migra silenciosamente a 'whisper-server'.
  const [engine, setEngine] = useState<SttEngine>(() => 'whisper-server');
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, engine);
  }, [engine]);

  const [supported, setSupported] = useState<boolean>(true);
  const [isListening, setIsListening] = useState<boolean>(false);
  const [transcript, setTranscript] = useState<string>('');
  const [finalTranscript, setFinalTranscript] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const [isTranscribing, setIsTranscribing] = useState<boolean>(false);
  const [modelLoadProgress, setModelLoadProgress] = useState<number>(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const recognitionRef = useRef<any>(null);

  // Detectar soporte según motor
  useEffect(() => {
    if (engine === 'google') {
      const SR =
        (window as any).SpeechRecognition ||
        (window as any).webkitSpeechRecognition;
      setSupported(!!SR);
    } else {
      setSupported(
        typeof window !== 'undefined' &&
          'MediaRecorder' in window &&
          'AudioContext' in window,
      );
    }
  }, [engine]);

  // Suscribirse al progreso de carga del modelo (solo whisper-local)
  useEffect(() => {
    if (engine !== 'whisper-local') return;
    whisperProgressCb = (p) => setModelLoadProgress(p);
    return () => {
      whisperProgressCb = null;
    };
  }, [engine]);

  const reset = useCallback(() => {
    setTranscript('');
    setFinalTranscript('');
    setError(null);
  }, []);

  /* ─────────── motor google ─────────── */

  const startGoogle = useCallback(() => {
    const SR =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SR) {
      setError('Tu navegador no soporta Web Speech API.');
      return;
    }
    try { recognitionRef.current?.stop(); } catch { /* noop */ }

    const rec = new SR();
    rec.lang = lang;
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onstart = () => setIsListening(true);
    rec.onresult = (event: any) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) final += res[0].transcript;
        else interim += res[0].transcript;
      }
      if (interim) setTranscript(interim);
      if (final) { setTranscript(final); setFinalTranscript(final); }
    };
    rec.onerror = (event: any) => {
      const code = event.error ?? 'unknown_error';
      const map: Record<string, string> = {
        'not-allowed':       'Permiso de micrófono denegado.',
        'service-not-allowed':'El navegador bloqueó el reconocimiento.',
        'no-speech':         'No te he oído. Vuelve a intentarlo.',
        'audio-capture':     'No se encontró ningún micrófono.',
        'network':           'Sin conexión con Google. Cambia a "Servidor" o "Navegador".',
        'aborted':           '',
      };
      const msg = map[code] ?? `Error de reconocimiento (${code})`;
      if (msg) setError(msg);
      setIsListening(false);
    };
    rec.onend = () => setIsListening(false);
    recognitionRef.current = rec;

    try { rec.start(); }
    catch (e: any) {
      setError(e?.message ?? 'No se pudo iniciar el micrófono.');
      setIsListening(false);
    }
  }, [lang]);

  const stopGoogle = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
  }, []);

  /* ─────────── motores basados en grabación ─────────── */

  /**
   * Inicia grabación. Al parar (`stop`), decodifica, convierte a WAV 16k mono
   * y llama al callback `onWav` con el blob WAV resultante.
   */
  const startRecording = useCallback(
    async (onWav: (wav: Blob) => Promise<void>) => {
      if (!('MediaRecorder' in window)) {
        setError('Tu navegador no soporta MediaRecorder.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        const mr = new MediaRecorder(stream);
        recordedChunksRef.current = [];

        mr.ondataavailable = (ev) => {
          if (ev.data.size > 0) recordedChunksRef.current.push(ev.data);
        };

        mr.onstop = async () => {
          streamRef.current?.getTracks().forEach((t) => t.stop());
          streamRef.current = null;

          const blob = new Blob(recordedChunksRef.current, { type: mr.mimeType });
          if (blob.size === 0) {
            setIsListening(false);
            return;
          }

          try {
            setIsTranscribing(true);
            const samples = await blobToMonoFloat32(blob);
            const wav = floatToWavBlob(samples);
            await onWav(wav);
          } catch (e: any) {
            setError(e?.message ?? 'Error procesando audio.');
          } finally {
            setIsTranscribing(false);
            setIsListening(false);
          }
        };

        mr.start();
        mediaRecorderRef.current = mr;
        setIsListening(true);
      } catch (e: any) {
        const name = e?.name ?? '';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          setError('Permiso de micrófono denegado. Habilítalo en la barra de direcciones.');
        } else if (name === 'NotFoundError') {
          setError('No se encontró ningún micrófono.');
        } else {
          setError(e?.message ?? 'No se pudo acceder al micrófono.');
        }
        setIsListening(false);
      }
    },
    [],
  );

  /* ─────────── motor whisper-server ─────────── */

  const startWhisperServer = useCallback(() => {
    return startRecording(async (wav: Blob) => {
      const formData = new FormData();
      formData.append('audio', wav, 'recording.wav');

      try {
        const { data } = await api.post<{ text: string }>('/ai/transcribe', formData);
        const text = (data?.text ?? '').trim();
        if (text) {
          setTranscript(text);
          setFinalTranscript(text);
        } else {
          setError('No se entendió nada. Vuelve a intentarlo.');
        }
      } catch (err: any) {
        const status = err?.response?.status;
        const apiMsg = err?.response?.data?.message;
        if (status === 503) {
          setError(apiMsg ?? 'Whisper no está configurado en el servidor.');
        } else if (status === 429) {
          setError(apiMsg ?? 'Demasiadas transcripciones por hora.');
        } else {
          setError(apiMsg ?? 'Error en el servidor al transcribir.');
        }
      }
    });
  }, [startRecording]);

  /* ─────────── motor whisper-local (transformers.js) ─────────── */

  const startWhisperLocal = useCallback(() => {
    return startRecording(async (wav: Blob) => {
      try {
        const samples = await blobToMonoFloat32(wav);
        const transcriber = await getWhisperLocal();
        const out = await transcriber(samples, {
          language: 'spanish',
          task: 'transcribe',
          chunk_length_s: 30,
          stride_length_s: 5,
        });
        const text = Array.isArray(out) ? out[0]?.text ?? '' : out.text ?? '';
        const clean = text.trim();
        if (clean) {
          setTranscript(clean);
          setFinalTranscript(clean);
        } else {
          setError('No se entendió nada. Vuelve a intentarlo.');
        }
      } catch (e: any) {
        resetWhisperLocal();
        setError('Error transcribiendo: ' + (e?.message ?? 'desconocido'));
      }
    });
  }, [startRecording]);

  const stopRecording = useCallback(() => {
    try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
  }, []);

  /* ─────────── API pública ─────────── */

  const start = useCallback(() => {
    reset();
    if (engine === 'google') startGoogle();
    else if (engine === 'whisper-server') startWhisperServer();
    else startWhisperLocal();
  }, [engine, reset, startGoogle, startWhisperServer, startWhisperLocal]);

  const stop = useCallback(() => {
    if (engine === 'google') stopGoogle();
    else stopRecording();
  }, [engine, stopGoogle, stopRecording]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop();
        mediaRecorderRef.current?.stop();
      } catch { /* noop */ }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return {
    engine,
    supported,
    isListening,
    isTranscribing,
    transcript,
    finalTranscript,
    error,
    modelLoadProgress,
    modelReady: engine === 'whisper-local' ? whisperPromise !== null : true,
    start,
    stop,
    reset,
    setEngine,
  };
}
